// helix-exception: dense badge text drawn at 8-11px in Figma has no Helix type-scale step (F-12)
import { AlertBanner } from '@/components/common/AlertBanner'
import { Plus, Refresh } from 'flowbite-react-icons/outline'
import { motion } from 'framer-motion'
import { withStopPropagation } from './cardSurface'
import { FormCard, StatusPill } from './FormCard'

interface PhenotypeGroup {
  title: string
  items: string[]
  isEmpty?: boolean
}

interface IndicationsCardProps {
  groups: PhenotypeGroup[]
  generatedAt?: string
  onEdit?: () => void
  /**
   * Secondary header action: jump straight to the Add Phenotypes (HPO search)
   * step in one click. Rendered as a lower-emphasis outlined pill left of Edit;
   * hidden while `isUpdating` (extraction in progress).
   */
  onAddPhenotypes?: () => void
  /**
   * When true, the Add Phenotypes button renders in a disabled state instead of
   * being interactive. Used on submitted/in-progress orders where the button is
   * shown for consistency but editing indications is not currently allowed.
   */
  addPhenotypesDisabled?: boolean
  isUpdating?: boolean
  needsAttention?: boolean
  /** R5: required section — show the purple left accent bar even when complete. */
  required?: boolean
  statusMessage?: string
  statusVariant?: 'success' | 'info'
}

const SKELETON_WIDTHS: number[][] = [[92, 78, 84, 69, 88], [80, 66, 90], [72]]

export function IndicationsCard({
  groups,
  generatedAt,
  onEdit,
  onAddPhenotypes,
  addPhenotypesDisabled = false,
  isUpdating = false,
  needsAttention = false,
  required = false,
  statusMessage,
  statusVariant = 'info',
}: IndicationsCardProps) {
  return (
    <FormCard
      title="Indications for Testing"
      onEdit={isUpdating ? undefined : onEdit}
      headerActions={
        !isUpdating && onAddPhenotypes ? (
          <button
            type="button"
            onClick={addPhenotypesDisabled ? undefined : withStopPropagation(onAddPhenotypes)}
            disabled={addPhenotypesDisabled}
            aria-disabled={addPhenotypesDisabled}
            className="inline-flex items-center justify-center gap-1 rounded-full border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-[10px] font-medium text-brand-600 dark:text-brand-400 transition-colors hover:bg-brand-50 dark:hover:bg-gray-800 disabled:cursor-not-allowed disabled:border-border-base dark:disabled:border-gray-700 disabled:bg-white dark:disabled:bg-gray-900 disabled:text-text-body dark:disabled:text-gray-400 disabled:opacity-50 disabled:hover:bg-white dark:disabled:hover:bg-gray-900 sm:gap-1.5 sm:px-2.5 sm:py-1.5 sm:text-xs"
          >
            <Plus className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            Add Phenotypes
          </button>
        ) : undefined
      }
      needsAttention={needsAttention}
      required={required}
      completed={!needsAttention}
      clickableSurface
    >
      {isUpdating ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col gap-4">
          <AlertBanner leftIcon={<Refresh className="h-4 w-4 shrink-0 text-text-fg-info" />}>
            Analyzing Clinical Summary to extract phenotypes...
          </AlertBanner>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-3">
            {['Key Phenotypes', 'Additional Phenotypes', 'Pertinent Negative'].map((title, index) => (
              <div key={title} className="flex flex-col gap-2 sm:gap-3">
                <h3 className="text-xs font-medium leading-5 text-text-heading dark:text-white sm:text-sm">{title}</h3>
                <div className="flex flex-col gap-1 sm:gap-2">
                  {SKELETON_WIDTHS[index].map((width, i) => (
                    <div
                      key={`${title}-${width}-${i}`}
                      className="h-5 animate-pulse rounded bg-background-quaternary dark:bg-gray-800"
                      style={{ width: `${width}%` }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-3">
            {groups.map((group) => (
              <div key={group.title} className="flex flex-col gap-2 sm:gap-3">
                <h3 className="text-xs font-medium leading-5 text-text-heading dark:text-white sm:text-sm">{group.title}</h3>
                <div className="flex flex-col gap-1 sm:gap-2">
                  {group.isEmpty || group.items.length === 0 ? (
                    <span className="text-sm leading-5 text-text-body-subtle dark:text-gray-500 sm:text-base sm:leading-6">
                      None entered
                    </span>
                  ) : (
                    group.items.map((item) => (
                      <span key={item} className="text-sm leading-5 text-text-body dark:text-gray-400 sm:text-base sm:leading-6">
                        {item}
                      </span>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>

          {statusMessage ? (
            <StatusPill
              text={generatedAt ? `${statusMessage} ${generatedAt}` : statusMessage}
              variant={statusVariant}
            />
          ) : (
            generatedAt && <StatusPill text={`Phenotypes generated ${generatedAt}`} variant="success" />
          )}
        </>
      )}
    </FormCard>
  )
}
